/**
 * EVERY TAG IN THE ROOM, in one place. They used to show only as filters, in a
 * pop-up behind "+ Narrow", with no word on what a category meant or how much
 * of the room was sorted on it.
 *
 * Each category says what it means and how much carries it; its values are
 * chips at the app's usual text size, each with its count. (They were a cloud,
 * up to 18px for the biggest - which read as a different site.) A click narrows the room to that value, the
 * same lens "+ Narrow" sets, and a second click takes it off.
 *
 * GROWING THE TAGS BY HAND. An axis can be given more starting values, and a
 * new axis added, here; Recalculate then sorts the board on them now rather
 * than at the next dig. The room's taxonomy library - a classification a
 * person pasted in - is kept here too, for the chat and the cartographer to
 * draw from. The chat can do all of this as well.
 */
import { useEffect, useState } from 'react';
import type { BoardroomAxisSkipped, BoardroomAxisWrite, BoardroomTagging, BoardroomTagList } from '../../types';
import { tagNegated, tagPicked } from './filters';

const SOURCE_WORDS: Record<string, string> = {
  axis: 'What the room compares on',
  content: 'What each thing is about',
  label: 'Labels added to count by',
};

/** "a, b\nc" -> ["a", "b", "c"]: values are typed comma- or line-separated. */
export function splitValues(text: string): string[] {
  return text
    .split(/[,\n]/)
    .map(v => v.trim())
    .filter(Boolean);
}

/** A category's meaning, its first phrase: what it is, without the sorter's
 *  examples and rules ("the person it is pitched to or used by, as a role:
 *  practice owner, … Skip it when …"). The whole is on hover. Pure. */
export function shortMeaning(meaning: string): string {
  const text = (meaning || '').trim();
  const cut = text.search(/[:;]|\.\s|\s[-—]\s/);
  const first = cut > 0 ? text.slice(0, cut).trim() : text;
  return first.length >= 12 ? first : text;
}

/** How many values used once each "+ more" shows. */
export const SINGLES_STEP = 20;

/** A category's values with the ones used ONCE folded away, `shown` of them
 *  let out. Buyer role had 54 values, most on one item each, and they filled
 *  the panel. A picked value always shows. Where nothing is used twice, the
 *  first step shows, so a category is never a bare "+ more". Pure. */
export function foldSingles<T extends { value: string; count: number }>(
  values: T[],
  isPicked: (v: T) => boolean,
  shown: number,
): { visible: T[]; hidden: number } {
  const singles = values.filter(v => v.count <= 1 && !isPicked(v));
  const step = values.length === singles.length ? Math.max(shown, SINGLES_STEP) : shown;
  const out = singles.slice(0, step);
  return { visible: values.filter(v => !singles.includes(v) || out.includes(v)), hidden: singles.length - out.length };
}

/** What the sorter is doing, in a line, for beside the Recalculate button. */
export function sortingLine(s: BoardroomTagging): string {
  if (s.after_budget?.used_up) return 'The sorting budget for the last 24 hours is spent.';
  if (s.working) return `Sorting… ${s.waiting.toLocaleString()} waiting`;
  if (s.waiting) return `${s.waiting.toLocaleString()} waiting to be sorted`;
  return 'Everything is sorted.';
}

const INPUT =
  'w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const SMALL_BTN =
  'rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700';

export default function TagsPanel({
  load,
  selected,
  onToggle,
  onRemove,
  onAddAxis,
  onRecalculate,
  onSaveTaxonomy,
  loadSkipped,
  onOpen,
  onClear,
  refreshKey,
}: {
  load: () => Promise<BoardroomTagList>;
  /** Changes when the board does, so the sorter's new tags show while it is open. */
  refreshKey?: string | number;
  /** "DIMENSION:value" keys in the lens now. */
  selected: string[];
  onToggle: (key: string) => void;
  /** Take one value off every item in the room. Asks first (the page does). */
  onRemove?: (dimension: string, value: string) => Promise<unknown>;
  /** Add an axis, or starting values to one. Answers the panel as it now stands. */
  onAddAxis?: (axis: BoardroomAxisWrite) => Promise<BoardroomTagList>;
  /** Sort the board now. Answers the sorter's counts. */
  onRecalculate?: () => Promise<BoardroomTagging>;
  onSaveTaxonomy?: (text: string) => Promise<unknown>;
  /** The items skipped on one axis, with why - opened under "skipped". */
  loadSkipped?: (dimension: string) => Promise<BoardroomAxisSkipped>;
  /** Open one item. */
  onOpen?: (itemId: number) => void;
  /** Take every tag off the lens. */
  onClear?: () => void;
}) {
  const [data, setData] = useState<BoardroomTagList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Which axis has its "+ value" box open, and what is typed in it.
  const [adding, setAdding] = useState<{ dimension: string; text: string } | null>(null);
  const [newAxis, setNewAxis] = useState<{ dimension: string; meaning: string; values: string } | null>(null);
  const [taxonomy, setTaxonomy] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // The axis whose skipped items are open, and what came back for it.
  const [skippedOpen, setSkippedOpen] = useState<string | null>(null);
  // Per category, how many of its values used once are let out.
  const [singlesShown, setSinglesShown] = useState<Record<string, number>>({});
  const [skipped, setSkipped] = useState<BoardroomAxisSkipped | 'loading' | 'failed' | null>(null);
  const toggleSkipped = (dimension: string) => {
    if (!loadSkipped) return;
    if (skippedOpen === dimension) {
      setSkippedOpen(null);
      return;
    }
    setSkippedOpen(dimension);
    setSkipped('loading');
    loadSkipped(dimension)
      .then(setSkipped)
      .catch(() => setSkipped('failed'));
  };
  useEffect(() => {
    load()
      .then(setData)
      .catch(e => setError(e instanceof Error ? e.message : 'Could not load the tags'));
  }, [load, refreshKey]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  };
  const addValues = (dimension: string, text: string) =>
    onAddAxis &&
    run(async () => {
      const values = splitValues(text);
      if (!values.length) return;
      setData(await onAddAxis({ dimension, values }));
      setAdding(null);
    });
  const addAxis = () =>
    onAddAxis &&
    newAxis &&
    run(async () => {
      setData(
        await onAddAxis({
          dimension: newAxis.dimension.trim(),
          meaning: newAxis.meaning.trim(),
          values: splitValues(newAxis.values),
        }),
      );
      setNewAxis(null);
    });
  const recalculate = () =>
    onRecalculate &&
    run(async () => {
      const sorting = await onRecalculate();
      setData(d => (d ? { ...d, sorting } : d));
    });

  if (!data) {
    return error ? (
      <p className="p-4 text-xs text-red-600 dark:text-red-400">{error}</p>
    ) : (
      <p className="p-4 text-xs text-gray-400">Reading the tags…</p>
    );
  }
  let lastSource = '';
  const firstNonAxis = data.categories.findIndex(c => c.source !== 'axis');
  const addAxisForm = onAddAxis && (
    <div className="mb-4" data-testid="add-axis">
      {newAxis ? (
        <div className="space-y-1.5 rounded border border-gray-200 p-2 dark:border-gray-700">
          <input
            className={INPUT}
            placeholder="Name, e.g. product category"
            aria-label="Axis name"
            value={newAxis.dimension}
            onChange={e => setNewAxis({ ...newAxis, dimension: e.target.value })}
          />
          <input
            className={INPUT}
            placeholder="What it sorts on, in one line"
            aria-label="What the axis means"
            value={newAxis.meaning}
            onChange={e => setNewAxis({ ...newAxis, meaning: e.target.value })}
          />
          <textarea
            className={INPUT}
            rows={2}
            placeholder="Starting values, comma separated: general BI, EHR, AI scribe"
            aria-label="Starting values"
            value={newAxis.values}
            onChange={e => setNewAxis({ ...newAxis, values: e.target.value })}
          />
          <div className="flex gap-1.5">
            <button type="button" className={SMALL_BTN} disabled={busy || !newAxis.dimension.trim()} onClick={() => void addAxis()}>
              Add axis
            </button>
            <button type="button" className={SMALL_BTN} onClick={() => setNewAxis(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className={SMALL_BTN} onClick={() => setNewAxis({ dimension: '', meaning: '', values: '' })}>
          + Add an axis
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-4 text-xs" data-testid="tags-panel">
      {error && <p className="mb-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
      {/* RECALCULATE: sort what is already on the board now, on the room's
          daily sorting budget, rather than waiting for the next dig. */}
      {onRecalculate && data.sorting && (
        <div className="mb-3 flex items-center gap-2 rounded bg-gray-50 px-2 py-1.5 dark:bg-gray-800" data-testid="sorting-bar">
          <span className="min-w-0 flex-1 text-gray-600 dark:text-gray-300">{sortingLine(data.sorting)}</span>
          <button
            type="button"
            className={SMALL_BTN}
            disabled={busy || !!data.sorting.after_budget?.used_up}
            onClick={() => void recalculate()}
            title="Sort the board into its tag groups now, instead of at the next dig. Tags a person placed are never moved."
            data-testid="recalculate"
          >
            Recalculate
          </button>
        </div>
      )}
      {data.categories.length === 0 ? (
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">Nothing is tagged yet. The sorter tags things during a dig.</p>
      ) : (
        <div className="mb-3 flex items-baseline gap-2">
          <p className="flex-1 text-xs text-gray-500 dark:text-gray-400">
            A tag narrows the whole room, the dashboard and every table; each one narrows further. To allow either of two, tick them together in the lens bar at the top.
          </p>
          {selected.length > 0 && onClear && (
            <button
              type="button"
              onClick={onClear}
              className="shrink-0 text-xs text-violet-600 hover:underline dark:text-violet-300"
              data-testid="tags-clear"
            >
              Clear all ({selected.length})
            </button>
          )}
        </div>
      )}
      {data.categories.map((c, i) => {
        const head = c.source !== lastSource;
        lastSource = c.source;
        return (
          <div key={c.dimension}>
            {i === firstNonAxis && addAxisForm}
            <section className="mb-4" data-testid={`tag-category-${c.dimension}`}>
              {head && (
                <h3 className="mb-2 border-b border-gray-100 pb-1 text-[11px] uppercase tracking-wide text-gray-400 dark:border-gray-700">
                  {SOURCE_WORDS[c.source] ?? c.source}
                </h3>
              )}
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-medium text-gray-800 dark:text-gray-100">{c.dimension.toLowerCase().replace(/_/g, ' ')}</span>
                {/* "HAS A VALUE", NOT "SORTED": "434 of 1,108 sorted" read as 674
                    still to do, when the sorter had looked at all of them and
                    found nothing on 674 that answers the axis. */}
                {c.source === 'axis' && (
                  <span className={`text-xs ${c.tagged * 2 < data.taggable ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'}`}>
                    {c.tagged.toLocaleString()} of {data.taggable.toLocaleString()} have a value
                    {!!c.skipped && (
                      <>
                        {' · '}
                        {loadSkipped ? (
                          <button
                            type="button"
                            onClick={() => toggleSkipped(c.dimension)}
                            aria-expanded={skippedOpen === c.dimension}
                            className="underline decoration-dotted hover:text-gray-700 dark:hover:text-gray-200"
                            title="Looked at, and nothing on the item answers this axis. Click to see which, and why."
                            data-testid={`skipped-${c.dimension}`}
                          >
                            {c.skipped.toLocaleString()} skipped
                          </button>
                        ) : (
                          `${c.skipped.toLocaleString()} skipped`
                        )}
                      </>
                    )}
                  </span>
                )}
              </div>
              {c.meaning && (
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400" title={c.meaning}>
                  {shortMeaning(c.meaning)}
                </p>
              )}
              {/* WHICH WERE SKIPPED, AND WHY: the sorter's own reason for each. */}
              {skippedOpen === c.dimension && (
                <div className="mt-1.5 max-h-64 overflow-y-auto rounded border border-gray-200 p-1.5 dark:border-gray-700" data-testid={`skipped-list-${c.dimension}`}>
                  {skipped === 'loading' || skipped === null ? (
                    <p className="text-gray-400">Reading what was skipped…</p>
                  ) : skipped === 'failed' ? (
                    <p className="text-red-600 dark:text-red-400">Could not load what was skipped.</p>
                  ) : (
                    <>
                      {skipped.items.map(it => (
                        <div key={it.id} className="py-0.5">
                          {onOpen ? (
                            <button type="button" onClick={() => onOpen(it.id)} className="text-left text-gray-800 hover:underline dark:text-gray-100">
                              {it.title}
                            </button>
                          ) : (
                            <span className="text-gray-800 dark:text-gray-100">{it.title}</span>
                          )}
                          {it.reason && <span className="text-gray-400"> - {it.reason}</span>}
                        </div>
                      ))}
                      {skipped.total > skipped.items.length && (
                        <p className="pt-1 text-gray-400">
                          and {(skipped.total - skipped.items.length).toLocaleString()} more
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
              {c.values.length === 0 && !c.unused?.length ? (
                <p className="mt-1 italic text-gray-400">nothing sorted on it yet</p>
              ) : (
                <div className="mt-1.5 flex flex-wrap items-baseline gap-1.5">
                  {(() => {
                    const fold = foldSingles(
                      c.values,
                      v => tagPicked(selected, `${c.dimension}:${v.value}`),
                      singlesShown[c.dimension] ?? 0,
                    );
                    return (
                      <>
                  {fold.visible.map(v => {
                    const key = `${c.dimension}:${v.value}`;
                    const on = tagPicked(selected, key);
                    const not = on && tagNegated(selected, key);
                    return (
                      <span key={key} className="group inline-flex items-baseline">
                      <button
                        type="button"
                        onClick={() => onToggle(key)}
                        aria-pressed={on}
                        title={`${v.count.toLocaleString()} item${v.count === 1 ? '' : 's'}${
                          not ? ' - left out by the lens now' : on ? ' - in the lens now' : ''
                        }`}
                        className={`rounded-full border px-2 py-0.5 text-xs leading-tight ${
                          not
                            ? 'border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-100'
                            : on
                            ? 'border-violet-500 bg-violet-600 text-white'
                            : 'border-violet-200 bg-violet-50 text-violet-800 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-900/30 dark:text-violet-200 dark:hover:bg-violet-900/50'
                        }`}
                        data-testid={`tag-${key}`}
                      >
                        {v.value} <span className={not ? 'text-amber-700 dark:text-amber-300' : on ? 'text-violet-100' : 'text-violet-400'}>{v.count.toLocaleString()}</span>
                      </button>
                      {/* HOVER ×: this value off EVERY item in the room. It lived on
                          the feed's tag list, which is gone now that tags have
                          this tab. Hidden until hover, so a chip is a filter first. */}
                      {onRemove && (
                        <button
                          type="button"
                          aria-label={`Remove tag ${c.dimension}:${v.value} from every item`}
                          title="Remove this tag from every item in the room"
                          onClick={() =>
                            void onRemove(c.dimension, v.value).then(() => load().then(setData).catch(() => undefined))
                          }
                          className="icon-btn px-1 text-gray-400 opacity-0 transition-opacity hover:text-red-500 focus:opacity-100 group-hover:opacity-100"
                        >
                          ×
                        </button>
                      )}
                      </span>
                    );
                  })}
                  {fold.hidden > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setSinglesShown(m => ({ ...m, [c.dimension]: (m[c.dimension] ?? 0) + SINGLES_STEP }))
                      }
                      className="text-xs text-violet-600 hover:underline dark:text-violet-300"
                      data-testid={`singles-${c.dimension}`}
                    >
                      + {Math.min(SINGLES_STEP, fold.hidden)} more used once
                      {fold.hidden > SINGLES_STEP ? ` (of ${fold.hidden.toLocaleString()})` : ''}
                    </button>
                  )}
                  {fold.hidden === 0 && (singlesShown[c.dimension] ?? 0) > 0 && (
                    <button
                      type="button"
                      onClick={() => setSinglesShown(m => ({ ...m, [c.dimension]: 0 }))}
                      className="text-xs text-gray-500 hover:underline dark:text-gray-400"
                    >
                      fewer
                    </button>
                  )}
                      </>
                    );
                  })()}
                  {/* STARTING VALUES nothing carries yet: the sorter will use them. */}
                  {c.unused?.map(v => (
                    <span
                      key={`unused-${v}`}
                      title="A starting value nothing carries yet. Recalculate to sort on it now."
                      className="rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-xs leading-tight text-gray-500 dark:border-gray-600 dark:text-gray-400"
                      data-testid={`unused-${c.dimension}:${v}`}
                    >
                      {v}
                    </span>
                  ))}
                  {c.more > 0 && <span className="text-xs text-gray-400">and {c.more} more</span>}
                </div>
              )}
              {c.source === 'axis' && onAddAxis && (
                <div className="mt-1.5">
                  {adding?.dimension === c.dimension ? (
                    <div className="flex gap-1.5">
                      <input
                        className={INPUT}
                        autoFocus
                        placeholder="New values, comma separated"
                        aria-label={`New values for ${c.dimension}`}
                        value={adding.text}
                        onChange={e => setAdding({ dimension: c.dimension, text: e.target.value })}
                        onKeyDown={e => {
                          if (e.key === 'Enter') void addValues(c.dimension, adding.text);
                          if (e.key === 'Escape') setAdding(null);
                        }}
                      />
                      <button type="button" className={SMALL_BTN} disabled={busy} onClick={() => void addValues(c.dimension, adding.text)}>
                        Add
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="text-xs text-indigo-600 hover:underline dark:text-indigo-300"
                      onClick={() => setAdding({ dimension: c.dimension, text: '' })}
                      data-testid={`add-values-${c.dimension}`}
                    >
                      + value
                    </button>
                  )}
                </div>
              )}
            </section>
          </div>
        );
      })}
      {firstNonAxis < 0 && addAxisForm}
      {/* THE TAXONOMY LIBRARY: kept as written. The chat and the cartographer
          take the axes and values that fit this room from it. */}
      {onSaveTaxonomy && (
        <details className="mt-2 border-t border-gray-100 pt-2 dark:border-gray-700" data-testid="taxonomy">
          <summary className="cursor-pointer text-[11px] uppercase tracking-wide text-gray-400">
            Taxonomy library {data.taxonomy ? '· kept' : ''}
          </summary>
          <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
            Paste a classification - axes, their labels, rules. It does not change the axes by itself: ask the chat to
            propose the ones that fit this room, or add them above.
          </p>
          <textarea
            className={`${INPUT} mt-1.5 font-mono`}
            rows={8}
            aria-label="Taxonomy library"
            value={taxonomy ?? data.taxonomy ?? ''}
            onChange={e => {
              setTaxonomy(e.target.value);
              setSaved(false);
            }}
          />
          <div className="mt-1.5 flex items-center gap-2">
            <button
              type="button"
              className={SMALL_BTN}
              disabled={busy || taxonomy === null}
              onClick={() =>
                void run(async () => {
                  await onSaveTaxonomy(taxonomy ?? '');
                  setData(d => (d ? { ...d, taxonomy: taxonomy ?? '' } : d));
                  setTaxonomy(null);
                  setSaved(true);
                })
              }
            >
              Save
            </button>
            {saved && <span className="text-xs text-gray-400">Saved.</span>}
          </div>
        </details>
      )}
    </div>
  );
}
